import {
  DialogBackdrop
} from "./JXNLCNWG.js";
import {
  usePreventBodyScroll
} from "./RBUEJWRA.js";
import {
  useNestedDialogs
} from "./T3WBUAHU.js";
import {
  useHideOnInteractOutside
} from "./KUXENZYT.js";
import {
  prependHiddenDismiss
} from "./62DFK33R.js";
import {
  markTreeOutside
} from "./BH4YAZB2.js";
import {
  disableTreeOutside
} from "./4H6H5RRM.js";
import {
  disableAccessibilityTreeOutside
} from "./7A3SODQV.js";
import {
  useFocusableContainer
} from "./6WVG5KFF.js";
import {
  HeadingLevel
} from "./6OMX4H3W.js";
import {
  DialogContext,
  DialogDescriptionContext,
  DialogHeadingContext
} from "./LNHZLQEK.js";
import {
  useFocusable
} from "./AGWM73EZ.js";
import {
  usePortal
} from "./USCWE5QL.js";
import {
  useDisclosureContent
} from "./6AJCHLAV.js";
import {
  createComponent,
  createElement,
  createHook
} from "./5ETIGB6O.js";
import {
  useBooleanEvent,
  useEvent,
  useForkRef,
  useId,
  usePortalRef,
  useSafeLayoutEffect,
  useWrapElement
} from "./ASYR66PQ.js";
import {
  __objRest,
  __spreadProps,
  __spreadValues
} from "./4BKCJXBM.js";

// src/dialog/dialog.tsx
import { useEffect, useRef, useState } from "react";
import {
  closest,
  contains,
  getActiveElement,
  getDocument,
  isButton
} from "@ariakit/core/utils/dom";
import {
  addGlobalEventListener,
  queueBeforeEvent
} from "@ariakit/core/utils/events";
import {
  focusIfNeeded,
  getFirstTabbableIn,
  isFocusable
} from "@ariakit/core/utils/focus";
import { chain } from "@ariakit/core/utils/misc";
import { isSafari } from "@ariakit/core/utils/platform";
import { jsx } from "react/jsx-runtime";
var isSafariBrowser = isSafari();
function isBackdrop(dialog, element) {
  const id = dialog.id;
  if (!id)
    return;
  return element.getAttribute("data-backdrop") === id;
}
function isAlreadyFocusingAnotherElement(dialog) {
  const activeElement = getActiveElement();
  if (!activeElement)
    return false;
  if (contains(dialog, activeElement))
    return false;
  if (isBackdrop(dialog, activeElement))
    return false;
  if (activeElement.hasAttribute("data-dialog"))
    return false;
  if (isFocusable(activeElement))
    return true;
  return false;
}
function getElementFromProp(prop, focusable = false) {
  if (!prop)
    return null;
  const element = "current" in prop ? prop.current : prop;
  if (!element)
    return null;
  if (focusable)
    return isFocusable(element) ? element : null;
  return element;
}
var useDialog = createHook(
  (_a) => {
    var _b = _a, {
      store,
      focusable = true,
      modal = true,
      portal = !!modal,
      backdrop = !!modal,
      backdropProps,
      hideOnEscape = true,
      hideOnInteractOutside = true,
      getPersistentElements,
      preventBodyScroll = !!modal,
      autoFocusOnShow = true,
      autoFocusOnHide = true,
      initialFocus,
      finalFocus
    } = _b, props = __objRest(_b, [
      "store",
      "focusable",
      "modal",
      "portal",
      "backdrop",
      "backdropProps",
      "hideOnEscape",
      "hideOnInteractOutside",
      "getPersistentElements",
      "preventBodyScroll",
      "autoFocusOnShow",
      "autoFocusOnHide",
      "initialFocus",
      "finalFocus"
    ]);
    const ref = useRef(null);
    const { portalRef, portalNode, domReady } = usePortalRef(
      portal,
      props.portalRef
    );
    const preserveTabOrderProp = props.preserveTabOrder;
    const preserveTabOrder = store.useState(
      (state) => preserveTabOrderProp && !modal && state.mounted
    );
    const id = useId(props.id);
    const open = store.useState("open");
    const mounted = store.useState("mounted");
    const contentElement = store.useState("contentElement");
    usePreventBodyScroll(store, mounted && preventBodyScroll);
    useHideOnInteractOutside(store, hideOnInteractOutside);
    const { wrapElement, nestedDialogs } = useNestedDialogs(store);
    props = useWrapElement(props, wrapElement, [wrapElement]);
    useEffect(() => {
      if (!open)
        return;
      const dialog = ref.current;
      const activeElement = getActiveElement(dialog, true);
      if (!activeElement)
        return;
      if (activeElement.tagName === "BODY")
        return;
      if (dialog && contains(dialog, activeElement))
        return;
      store.setDisclosureElement(activeElement);
    }, [open]);
    if (isSafariBrowser) {
      useEffect(() => {
        if (!mounted)
          return;
        const { disclosureElement } = store.getState();
        if (!disclosureElement)
          return;
        if (!isButton(disclosureElement))
          return;
        const onMouseDown = () => {
          let receivedFocus = false;
          const onFocus = () => {
            receivedFocus = true;
          };
          const options = { capture: true, once: true };
          disclosureElement.addEventListener("focusin", onFocus, options);
          queueBeforeEvent(disclosureElement, "mouseup", () => {
            disclosureElement.removeEventListener("focusin", onFocus, true);
            if (receivedFocus)
              return;
            focusIfNeeded(disclosureElement);
          });
        };
        disclosureElement.addEventListener("mousedown", onMouseDown);
        return () => {
          disclosureElement.removeEventListener("mousedown", onMouseDown);
        };
      }, [mounted]);
    }
    const shouldDisableAccessibilityTree = modal || // Usually, we only want to disable the accessibility tree outside if the
    // dialog is a modal. But the Portal component can't preserve the tab
    // order on Safari/VoiceOver. By allowing only the dialog/portal to be
    // accessible, we provide a similar tab order flow. We don't need to
    // disable pointer events because it's just for screen readers.
    portal && preserveTabOrder && isSafari();
    useEffect(() => {
      if (!mounted)
        return;
      if (!domReady)
        return;
      const dialog = ref.current;
      if (!dialog)
        return;
      if (!shouldDisableAccessibilityTree)
        return;
      const existingDismiss = dialog.querySelector("[data-dialog-dismiss]");
      if (existingDismiss)
        return;
      return prependHiddenDismiss(dialog, store.hide);
    }, [mounted, domReady, shouldDisableAccessibilityTree]);
    const getPersistentElementsProp = useEvent(getPersistentElements);
    useSafeLayoutEffect(() => {
      if (!id)
        return;
      if (!open)
        return;
      const { disclosureElement } = store.getState();
      const element = portal ? portalNode : ref.current;
      const persistentElements = getPersistentElementsProp() || [];
      const allElements = [element, ...nestedDialogs, ...persistentElements];
      if (!shouldDisableAccessibilityTree) {
        return markTreeOutside(id, disclosureElement, ...allElements);
      }
      if (modal) {
        return chain(
          markTreeOutside(id, ...allElements),
          disableTreeOutside(...allElements)
        );
      }
      return chain(
        markTreeOutside(id, disclosureElement, ...allElements),
        disableAccessibilityTreeOutside(...allElements)
      );
    }, [
      id,
      open,
      store,
      portal,
      portalNode,
      nestedDialogs,
      getPersistentElementsProp,
      shouldDisableAccessibilityTree,
      modal
    ]);
    const mayAutoFocusOnShow = !!autoFocusOnShow;
    const autoFocusOnShowProp = useBooleanEvent(autoFocusOnShow);
    const [autoFocusEnabled, setAutoFocusEnabled] = useState(false);
    useEffect(() => {
      if (!open)
        return;
      if (!mayAutoFocusOnShow)
        return;
      if (!domReady)
        return;
      if (!(contentElement == null ? void 0 : contentElement.isConnected))
        return;
      const element = getElementFromProp(initialFocus, true) || // If no initial focus is specified, we try to focus the first element
      // with the autofocus attribute. If it's an Ariakit component, the
      // Focusable component will consume the autoFocus prop and add the
      // data-autofocus attribute to the element instead.
      contentElement.querySelector(
        "[data-autofocus=true],[autofocus]"
      ) || // We have to fallback to the first focusable element otherwise portaled
      // dialogs with preserveTabOrder set to true will not receive focus
      // properly because the elements aren't tabbable until the dialog
      // receives focus.
      getFirstTabbableIn(contentElement, true, portal && preserveTabOrder) || // Finally, we fallback to the dialog element itself.
      contentElement;
      if (!autoFocusOnShowProp(element))
        return;
      setAutoFocusEnabled(true);
      element.focus();
    }, [
      open,
      mayAutoFocusOnShow,
      domReady,
      contentElement,
      initialFocus,
      portal,
      preserveTabOrder,
      autoFocusOnShowProp
    ]);
    const mayAutoFocusOnHide = !!autoFocusOnHide;
    const autoFocusOnHideProp = useBooleanEvent(autoFocusOnHide);
    const [hasOpened, setHasOpened] = useState(false);
    useEffect(() => {
      if (!open)
        return;
      setHasOpened(true);
      return () => setHasOpened(false);
    }, [open]);
    useSafeLayoutEffect(() => {
      if (!hasOpened)
        return;
      if (!mayAutoFocusOnHide)
        return;
      const dialog = ref.current;
      const focusOnHide = (retry = true) => {
        if (!dialog)
          return;
        if (isAlreadyFocusingAnotherElement(dialog))
          return;
        const { disclosureElement } = store.getState();
        let element = getElementFromProp(finalFocus) || disclosureElement;
        if (!element)
          return;
        if (element.id) {
          const doc = getDocument(element);
          const selector = `[aria-activedescendant="${element.id}"]`;
          const composite = doc.querySelector(selector);
          if (composite) {
            element = composite;
          }
        }
        if (!isFocusable(element)) {
          const maybeParentDialog = closest(element, "[data-dialog]");
          if (maybeParentDialog && maybeParentDialog.id) {
            const doc = getDocument(maybeParentDialog);
            const selector = `[aria-controls~="${maybeParentDialog.id}"]`;
            const control = doc.querySelector(selector);
            if (control) {
              element = control;
            }
          }
        }
        if (!isFocusable(element)) {
          if (!retry)
            return;
          requestAnimationFrame(() => focusOnHide(false));
          return;
        }
        if (!autoFocusOnHideProp(element))
          return;
        element.focus();
      };
      if (!open) {
        return focusOnHide();
      }
      return focusOnHide;
    }, [hasOpened, open, mayAutoFocusOnHide, finalFocus, autoFocusOnHideProp]);
    const hideOnEscapeProp = useBooleanEvent(hideOnEscape);
    useEffect(() => {
      if (!domReady)
        return;
      if (!mounted)
        return;
      const onKeyDown = (event) => {
        if (event.key !== "Escape")
          return;
        if (event.defaultPrevented)
          return;
        const dialog = ref.current;
        if (!dialog)
          return;
        const target = event.target;
        if (!target)
          return;
        const { disclosureElement } = store.getState();
        const isValidTarget = () => {
          if (target.tagName === "BODY")
            return true;
          if (contains(dialog, target))
            return true;
          if (!disclosureElement)
            return false;
          if (contains(disclosureElement, target))
            return true;
          return false;
        };
        if (!isValidTarget())
          return;
        if (!hideOnEscapeProp(event))
          return;
        event.stopImmediatePropagation();
        store.hide();
      };
      return addGlobalEventListener("keydown", onKeyDown);
    }, [mounted, domReady, hideOnEscapeProp]);
    props = useWrapElement(
      props,
      (element) => /* @__PURE__ */ jsx(HeadingLevel, { level: modal ? 1 : void 0, children: element }),
      [modal]
    );
    const hiddenProp = props.hidden;
    props = useWrapElement(
      props,
      (element) => {
        if (backdrop) {
          return /* @__PURE__ */ jsx(
            DialogBackdrop,
            {
              store,
              backdrop,
              backdropProps,
              hideOnInteractOutside,
              hideOnEscape,
              hidden: hiddenProp,
              children: element
            }
          );
        }
        return element;
      },
      [
        store,
        backdrop,
        backdropProps,
        hideOnInteractOutside,
        hideOnEscape,
        hiddenProp
      ]
    );
    const [headingId, setHeadingId] = useState();
    const [descriptionId, setDescriptionId] = useState();
    props = useWrapElement(
      props,
      (element) => /* @__PURE__ */ jsx(DialogContext.Provider, { value: store, children: /* @__PURE__ */ jsx(DialogHeadingContext.Provider, { value: setHeadingId, children: /* @__PURE__ */ jsx(DialogDescriptionContext.Provider, { value: setDescriptionId, children: element }) }) }),
      [store]
    );
    props = __spreadProps(__spreadValues({
      id,
      "data-dialog": "",
      role: "dialog",
      tabIndex: focusable ? -1 : void 0,
      "aria-labelledby": headingId,
      "aria-describedby": descriptionId
    }, props), {
      ref: useForkRef(ref, props.ref)
    });
    props = useFocusableContainer(__spreadProps(__spreadValues({}, props), {
      autoFocusOnShow: autoFocusEnabled
    }));
    props = useDisclosureContent(__spreadValues({ store }, props));
    props = useFocusable(__spreadProps(__spreadValues({}, props), { focusable }));
    props = usePortal(__spreadProps(__spreadValues({ portal }, props), { portalRef, preserveTabOrder }));
    return props;
  }
);
var Dialog = createComponent((props) => {
  const htmlProps = useDialog(props);
  return createElement("div", htmlProps);
});
if (process.env.NODE_ENV !== "production") {
  Dialog.displayName = "Dialog";
}

export {
  useDialog,
  Dialog
};
